/**
 * Visual tuning constants shared across the Circles render subsystem.
 * Centralised here so a future pass can adjust the "feel" of the
 * playfield without hunting through every draw module.
 */

/** Star count for the parallax bg layer. */
export const STAR_COUNT = 90;

/**
 * Maximum number of cursor positions kept in the trail buffer. ~24
 * samples at 60 FPS = a 0.4-second tail, which reads as a snappy
 * motion ribbon without trailing into a long smear that distracts
 * from the circles themselves.
 */
export const CURSOR_TRAIL_MAX = 24;

/**
 * Animation tuning for the cursor "press" pulse. `IDLE_SCALE` is the
 * resting size multiplier (1.0 = baseline reticle), `PRESSED_SCALE`
 * is the held-down size, and `LERP_RATE` is the per-second easing
 * coefficient — exponential approach, so high values snap faster.
 *
 * Picked so the press feels snappy (≈80ms to reach ~95% of target)
 * without the cursor jiggling during fast tap-and-release patterns.
 */
export const CURSOR_IDLE_SCALE = 1;
export const CURSOR_PRESSED_SCALE = 1.45;
export const CURSOR_SCALE_LERP_RATE = 28;

/**
 * Combo thresholds at which the HUD combo counter pulses harder than
 * a normal increment. Mirrors `circlesSession.COMBO_MILESTONES` —
 * kept private here so the renderer doesn't import session internals.
 */
export const COMBO_MILESTONE_THRESHOLDS = [10, 25, 50, 100] as const;

/**
 * Combo at which the background's radial bloom starts cycling its
 * hue through the combo palette. Below this threshold the bloom uses
 * the chart's pad colour — keeps early play visually stable and
 * reserves the colour shift as a reward for sustained play.
 */
export const COMBO_HUE_CYCLE_THRESHOLD = 50;

/**
 * Kiai effect tuning. Kiai is osu's "this is the climax / hook"
 * marker — during these beat ranges the playfield gets brighter and
 * pulses harder in time with the beat, and entry into each section
 * is punctuated by a one-time radial star burst so the player feels
 * the music shifting gear.
 *
 * Particle count is chosen to feel celebratory without overwhelming
 * the playfield (24 is roughly half of `HIT_BURST_MAX` × 2 layers,
 * so a kiai entry mid-stream still reads as distinct from a flurry
 * of hits). Speed and life are tuned so the burst clears the visible
 * playfield by the time the second beat of kiai lands.
 */
export const KIAI_BURST_PARTICLE_COUNT = 24;
export const KIAI_BURST_SPEED = 320;
export const KIAI_BURST_LIFE = 0.85;
/**
 * Peak alpha of the warm overlay drawn over the bg during kiai. The
 * overlay is multiplied by the per-beat envelope (1 at downbeat → 0
 * at the next beat, cubic ease-out), so the playfield throbs in time
 * with the music rather than just sitting brighter.
 */
export const KIAI_OVERLAY_ALPHA_PEAK = 0.16;
/**
 * Hit-circle scale boost during a kiai beat. Scales `1 + KIAI_HIT_SCALE_PEAK
 * × envelope`, so a circle drawn on the downbeat is up to 4% larger
 * than its normal size — a subtle "alive" wobble in time with the
 * music. Tied to the same per-beat envelope as the bg overlay.
 */
export const KIAI_HIT_SCALE_PEAK = 0.04;

/**
 * Spring physics for the cursor trail follower. Slightly under-critical
 * (`damping < 2*sqrt(stiffness)` ≈ 40) so a hard flick produces a
 * single small overshoot before settling — that's the "weight" the
 * brief calls for. Reduced-motion users skip the spring entirely
 * (the trail itself is suppressed in `updateCursorTrail`).
 */
export const TRAIL_SPRING_STIFFNESS = 400;
export const TRAIL_SPRING_DAMPING = 28;

/**
 * Slider visual constants. The body is rendered as a constant-width
 * "track" with a coloured border and a darker inner fill; the ball is
 * a small filled disc that traces the path during the active phase;
 * the follow circle is a larger ring whose radius matches the engine's
 * tracking radius (visual feedback for "is my cursor close enough to
 * track?").
 *
 * Body width is sized as a multiple of the chart's hit-circle radius
 * — osu does the same so a slider's width matches the head/tail
 * caps. 1.0 fills the radius cleanly so the head's combo number
 * still reads against the darker track interior.
 *
 * Body rendering is done on an offscreen canvas at full opacity, then
 * composited back at {@link SLIDER_BODY_COMPOSITE_ALPHA}. This is the
 * critical trick: a single stroke pass on the main canvas would
 * compound opacity wherever the slider path crosses itself (teardrop /
 * horseshoe shapes), producing dark blobs where the eye expects
 * uniform thickness. Painting at full opacity into an offscreen layer
 * collapses overlapping segments to the same colour as
 * non-overlapping ones, so the resulting track reads as a constant-
 * width ribbon regardless of self-intersection.
 *
 * Fade-out grace lets the slider's afterimage linger briefly past
 * `endBeat` so the visual end matches the engine's tail-event timing
 * (which fires AT endBeat).
 */
export const SLIDER_FADE_OUT_BEATS = 0.3;
export const SLIDER_BODY_WIDTH_MUL = 1.0;
export const SLIDER_BALL_RADIUS_MUL = 0.55;
/**
 * Slider tick markers — small white diamonds spaced evenly along the
 * body as a built-in ruler for a slider's on-screen length (positions
 * from {@link import('@shared/osuSliderTiming').sliderTickDistances}).
 * Closely-packed diamonds read as a short slider, widely-spaced ones as
 * a long one. The slider ball "eats" each diamond as it sweeps over it
 * and they return for the next slide.
 *
 *   - `SLIDER_TICK_SPACING_MUL` — gap between diamonds as a multiple of
 *     the hit radius (osu units). This is the knob for how often
 *     diamonds appear: LOWER it for a denser ruler that shows on more /
 *     shorter sliders, raise it for a sparser one. Deliberately a fixed
 *     distance, decoupled from the chart's beat-based slider tick rate,
 *     so density tracks visible length rather than beats.
 *   - `SLIDER_TICK_RADIUS_MUL` — diamond half-diagonal (centre→vertex)
 *     as a multiple of the (kiai-boosted) hit radius. Small enough to
 *     sit inside the body track's dark interior without touching the
 *     coloured rim.
 *   - `SLIDER_TICK_MIN_RADIUS_PX` — pixel floor so diamonds stay
 *     visible on small / low-res playfields.
 *   - `SLIDER_TICK_ALPHA` — peak fill opacity (further scaled by the
 *     slider's lifecycle fade-in/out).
 *   - `SLIDER_TICK_POP_DISTANCE_MUL` — distance window, as a multiple
 *     of the hit radius IN OSU UNITS, over which a diamond fades + swells
 *     once the ball sweeps past it on the current span. Expressed in
 *     osu units (not pixels) because it's compared against the ball's
 *     arc-length, which lives in playfield-coordinate space.
 *   - `SLIDER_TICK_POP_SWELL` — extra size (fraction of the base
 *     radius) a diamond grows to at the peak of its pop.
 *   - `SLIDER_TICK_RESPAWN_BEATS` — on a repeat slider the diamonds a
 *     span ate are re-collected on the next pass; they fade back in
 *     over this many beats from the bounce so the reset matches the
 *     smooth fades elsewhere instead of snapping on. The renderer caps
 *     the window to half the span, so it still completes on very-short
 *     (high-SV) repeat spans.
 */
export const SLIDER_TICK_SPACING_MUL = 1.4;
export const SLIDER_TICK_RADIUS_MUL = 0.17;
export const SLIDER_TICK_MIN_RADIUS_PX = 2.5;
export const SLIDER_TICK_ALPHA = 0.9;
export const SLIDER_TICK_POP_DISTANCE_MUL = 0.55;
export const SLIDER_TICK_POP_SWELL = 0.6;
export const SLIDER_TICK_RESPAWN_BEATS = 0.1;
/**
 * Final composite alpha when blitting the offscreen slider body onto
 * the main canvas. < 1 so circles previously laid down beneath aren't
 * fully obscured (a slider crossing a still-visible upcoming circle
 * should let the circle read through), but high enough that the track
 * still reads as a defined ribbon.
 */
export const SLIDER_BODY_COMPOSITE_ALPHA = 0.92;
/**
 * Border-to-body width ratio for the offscreen render. The outer
 * stroke is the full body width (combo colour); the inner stroke
 * trims that fraction of the body width and is a desaturated darker
 * version of the combo colour, leaving a visible "rim" around the
 * track and a clear dark interior that the head's number sits over.
 */
export const SLIDER_BODY_INNER_RATIO = 0.78;

/**
 * Hit-burst lifetime + size envelopes. Spawned per successful hit
 * (perfect / good / ok) at the circle's CSS centre. Two layered
 * effects scale outward together to give the iconic osu! "tap →
 * tiny pop" feel:
 *
 *   1. Body burst — filled disc that grows 1.0× → 1.4× the chart's
 *      hit-circle radius over {@link HIT_BURST_BODY_LIFE} seconds
 *      with cubic ease-out and fades. This is the "scale-burst on
 *      hit" detail every osu skin renders.
 *
 *   2. Ring puff — stroked ring that grows 1.0× → 1.5× the body's
 *      starting radius over {@link HIT_BURST_RING_LIFE} seconds
 *      (faster than the body so they don't co-occlude). Stand-in
 *      for the approach-ring "click burst" that osu skins render
 *      as the approach circle disappears outward instead of just
 *      vanishing.
 *
 * Both are bounded by {@link HIT_BURST_MAX} so a dense stream can't
 * unbounded-grow the per-frame draw list.
 */
export const HIT_BURST_BODY_LIFE = 0.2;
export const HIT_BURST_BODY_END_SCALE = 1.4;
export const HIT_BURST_RING_LIFE = 0.12;
export const HIT_BURST_RING_END_SCALE = 1.5;
/**
 * Whichever layer lives longest dictates when the burst entry can
 * be culled. Hoisted to a module const so cull passes don't re-Max
 * twice and so a future tuning that flips which layer is longer-lived
 * stays automatically correct.
 */
export const HIT_BURST_MAX_LIFE = Math.max(HIT_BURST_BODY_LIFE, HIT_BURST_RING_LIFE);
/**
 * Realistic stream density at 220 BPM 1/4 = ~14.7 hits/sec × 0.2 s
 * burst life = ~3 simultaneous bursts. 12 gives 4× headroom while
 * halving the worst-case shadow-blur load vs the previous (stroke-
 * only) cap of 24.
 */
export const HIT_BURST_MAX = 12;

/**
 * Dark "contrast rim" drawn just outside each hit circle's coloured
 * border. This is the single biggest readability win for overlapping /
 * stacked circles: a thin dark ring gives every circle a hard edge, so
 * when two (or ten) pile up — at a slider's end, a stream stack, or a
 * circle sneaking up under another — the eye sees a crisp dark crescent
 * between them instead of two same-coloured discs bleeding into one
 * blob.
 *
 * It is the missing half of osu!'s stack-readability recipe: osu offsets
 * stacks diagonally (we do too, see {@link import('../circlesStacking').getStackOffsets})
 * AND draws hit circles with a dark border/overlay so each disc reads as
 * a distinct token. We had the offset but not the border.
 *
 * Why it doesn't make dense stacks noisier: the rim is dark and sits on
 * top of whatever's behind it, so a 10-high stack collapses into a clean
 * dark-edged staircase rather than a brighter, mushier blob. On the empty
 * playfield (dark bg) the rim is near-invisible — it only "appears" where
 * it overlaps something bright, i.e. exactly when separation is needed.
 *
 *   - `CIRCLE_RIM_RADIUS_MUL` — rim centre-line radius as a multiple of
 *     the hit radius. Sits just beyond the coloured border, whose outer
 *     edge is at ~1.06× (innerR + half the border stroke width).
 *   - `CIRCLE_RIM_WIDTH_MUL` — rim stroke width as a multiple of the hit
 *     radius. Drawn as a stroke (not a filled disc) so it never darkens
 *     the circle's own semi-transparent body, only the annulus outside it.
 *   - `CIRCLE_RIM_ALPHA` — peak opacity (further multiplied by the
 *     circle's fade-in so the rim emerges with the body).
 *   - `CIRCLE_RIM_COLOUR` — near-black navy; contrasts against bright
 *     circle bodies while blending into the dark playfield.
 */
export const CIRCLE_RIM_RADIUS_MUL = 1.12;
export const CIRCLE_RIM_WIDTH_MUL = 0.14;
export const CIRCLE_RIM_ALPHA = 0.85;
export const CIRCLE_RIM_COLOUR = '#05060d';

/**
 * Shake-jitter applied to a circle's rendered position after an
 * early-click. Mirrors osu! lazer's "shake but don't consume" feel:
 * the circle still sits at its real centre for hit-detection (the
 * engine is unchanged) but visually it wobbles for SHAKE_LIFE
 * seconds so the player sees their click was acknowledged on the
 * right target, just too early.
 *
 * `SHAKE_FREQUENCY` is in cycles per second — high enough for the
 * jitter to read as a vibration, low enough that the eye still
 * tracks the circle through it.
 *
 * `SHAKE_AMPLITUDE_OSU` is the peak horizontal swing in osu! units;
 * the renderer scales it through the playfield letterbox factor so
 * it stays canvas-relative.
 */
export const SHAKE_LIFE = 0.18;
export const SHAKE_FREQUENCY = 22;
export const SHAKE_AMPLITUDE_OSU = 6;

/**
 * Hit-error meter — the osu! staple. Logs the last
 * `HIT_ERROR_HISTORY_MAX` tap timings and renders them as coloured
 * ticks along a horizontal strip at the bottom of the playfield.
 * Players learn whether they're consistently early or late, which
 * is the single highest-leverage piece of feedback in any timing
 * game.
 *
 * `HIT_ERROR_LIFE` is the total seconds a tick lingers before being
 * culled. `HIT_ERROR_FADE_START` is the age at which the tick begins
 * fading out — older ticks dim while newer ones stay opaque so the
 * eye is always drawn to current performance.
 *
 * `HIT_ERROR_MEAN_TRIGGER` is the minimum number of recent ticks
 * needed before we draw the orange average-indicator arrow. Too few
 * samples and the average jitters wildly between every hit; this
 * threshold lets it settle.
 */
export const HIT_ERROR_HISTORY_MAX = 30;
export const HIT_ERROR_LIFE = 4.0;
export const HIT_ERROR_FADE_START = 2.5;
export const HIT_ERROR_MEAN_TRIGGER = 5;

/** Particle pool cap — heavy bursts on a long song can otherwise unbounded-grow. */
export const PARTICLE_POOL_MAX = 600;

/** Shockwave pool cap — caps per-frame stroke calls on rapid-fire hits. */
export const SHOCKWAVE_POOL_MAX = 32;

/** Score-pop pool cap — caps per-frame text draws on rapid-fire hits. */
export const SCORE_POP_POOL_MAX = 24;
